// packages/shared/api-client/endpoints/forum.js
// Forum API endpoints

import { baseApi } from "../baseApi";

/**
 * Forum API endpoints
 * Provides threads, replies, reactions, and category management
 */
const forumApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // -----------------------------------------------------------------------
    // CATEGORIES
    // -----------------------------------------------------------------------

    // Get all forum categories
    getForumCategories: builder.query({
      query: () => "/api/forum/categories",
      providesTags: ["ForumCategory"],
    }),

    // Create category (MANAGE_FORUM)
    createForumCategory: builder.mutation({
      query: (data) => ({
        url: "/api/forum/categories",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ForumCategory"],
    }),

    // Update category (MANAGE_FORUM)
    updateForumCategory: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/forum/categories/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["ForumCategory"],
    }),

    // Delete category (MANAGE_FORUM)
    deleteForumCategory: builder.mutation({
      query: (id) => ({
        url: `/api/forum/categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ForumCategory"],
    }),

    // -----------------------------------------------------------------------
    // THREADS
    // -----------------------------------------------------------------------

    // Get paginated thread list; params: { page, limit, categoryId, departmentId, search }
    getForumThreads: builder.query({
      query: (params = {}) => ({
        url: "/api/forum",
        params,
      }),
      providesTags: (result) =>
        result?.data?.threads
          ? [
              ...result.data.threads.map(({ id }) => ({ type: "ForumThread", id })),
              { type: "ForumThread", id: "LIST" },
            ]
          : [{ type: "ForumThread", id: "LIST" }],
    }),

    // Get single thread with replies
    getForumThreadById: builder.query({
      query: (id) => `/api/forum/${id}`,
      providesTags: (result, error, id) => [
        { type: "ForumThread", id },
        { type: "ForumReply", id: `THREAD_${id}` },
      ],
    }),

    // Create thread
    createForumThread: builder.mutation({
      query: (data) => ({
        url: "/api/forum",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "ForumThread", id: "LIST" }],
    }),

    // Edit thread (own or MANAGE_FORUM)
    updateForumThread: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/forum/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ForumThread", id },
        { type: "ForumThread", id: "LIST" },
      ],
    }),

    // Delete thread (own or MANAGE_FORUM)
    deleteForumThread: builder.mutation({
      query: (id) => ({
        url: `/api/forum/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ForumThread", id: "LIST" }],
    }),

    // Toggle lock (MANAGE_FORUM)
    toggleLockThread: builder.mutation({
      query: (id) => ({
        url: `/api/forum/${id}/lock`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "ForumThread", id },
        { type: "ForumThread", id: "LIST" },
      ],
    }),

    // Toggle pin (MANAGE_FORUM or PIN_FORUM_THREADS)
    togglePinThread: builder.mutation({
      query: (id) => ({
        url: `/api/forum/${id}/pin`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "ForumThread", id },
        { type: "ForumThread", id: "LIST" },
      ],
    }),

    // -----------------------------------------------------------------------
    // REACTIONS
    // -----------------------------------------------------------------------

    // Add or toggle reaction; body: { reactionType: "like" | "dislike" }
    reactToThread: builder.mutation({
      query: ({ id, reactionType }) => ({
        url: `/api/forum/${id}/reactions`,
        method: "POST",
        body: { reactionType },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "ForumThread", id }],
    }),

    // Remove reaction
    removeThreadReaction: builder.mutation({
      query: (id) => ({
        url: `/api/forum/${id}/reactions`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [{ type: "ForumThread", id }],
    }),

    // -----------------------------------------------------------------------
    // REPLIES
    // -----------------------------------------------------------------------

    // Add reply to thread
    addForumReply: builder.mutation({
      query: ({ threadId, content }) => ({
        url: `/api/forum/${threadId}/replies`,
        method: "POST",
        body: { content },
      }),
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: "ForumThread", id: threadId },
        { type: "ForumReply", id: `THREAD_${threadId}` },
      ],
    }),

    // Edit reply (own or MANAGE_FORUM)
    updateForumReply: builder.mutation({
      query: ({ threadId, replyId, content }) => ({
        url: `/api/forum/${threadId}/replies/${replyId}`,
        method: "PUT",
        body: { content },
      }),
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: "ForumThread", id: threadId },
        { type: "ForumReply", id: `THREAD_${threadId}` },
      ],
    }),

    // Delete reply — soft delete (own or MANAGE_FORUM)
    deleteForumReply: builder.mutation({
      query: ({ threadId, replyId }) => ({
        url: `/api/forum/${threadId}/replies/${replyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { threadId }) => [
        { type: "ForumThread", id: threadId },
        { type: "ForumReply", id: `THREAD_${threadId}` },
      ],
    }),
  }),
});

// Categories
export const useGetForumCategoriesQuery = forumApi.useGetForumCategoriesQuery;
export const useCreateForumCategoryMutation = forumApi.useCreateForumCategoryMutation;
export const useUpdateForumCategoryMutation = forumApi.useUpdateForumCategoryMutation;
export const useDeleteForumCategoryMutation = forumApi.useDeleteForumCategoryMutation;

// Threads
export const useGetForumThreadsQuery = forumApi.useGetForumThreadsQuery;
export const useGetForumThreadByIdQuery = forumApi.useGetForumThreadByIdQuery;
export const useCreateForumThreadMutation = forumApi.useCreateForumThreadMutation;
export const useUpdateForumThreadMutation = forumApi.useUpdateForumThreadMutation;
export const useDeleteForumThreadMutation = forumApi.useDeleteForumThreadMutation;
export const useToggleLockThreadMutation = forumApi.useToggleLockThreadMutation;
export const useTogglePinThreadMutation = forumApi.useTogglePinThreadMutation;

// Reactions
export const useReactToThreadMutation = forumApi.useReactToThreadMutation;
export const useRemoveThreadReactionMutation = forumApi.useRemoveThreadReactionMutation;

// Replies
export const useAddForumReplyMutation = forumApi.useAddForumReplyMutation;
export const useUpdateForumReplyMutation = forumApi.useUpdateForumReplyMutation;
export const useDeleteForumReplyMutation = forumApi.useDeleteForumReplyMutation;
